import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { adminDownloadBookingsCsv, adminProfitReport } from '../../api/adminBookings';
import AdminShell, { useAdminAccess } from './AdminShell';
import { ProfitTiles, daysAgoIso, margin, money, productLabel, productTypes } from './bookingFormat';
import { can } from './access';

const presets = [['7 days', 6], ['30 days', 29], ['90 days', 89], ['12 months', 364]];

/** Sales, supplier cost and profit by product, supplier and day or month. */
export default function AdminReportsPage() {
  const { auth, user } = useAuth();
  const hasAccess = useAdminAccess();
  const [range, setRange] = useState({ from: daysAgoIso(29), to: daysAgoIso(0) });
  const [product, setProduct] = useState('');
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setError('');
      setReport(await adminProfitReport(auth.accessToken, { ...range, product }));
    } catch (requestError) {
      setError(requestError.message);
    }
  }, [auth?.accessToken, range, product]);

  useEffect(() => { if (hasAccess) load(); }, [hasAccess, load]);

  const exportCsv = () => adminDownloadBookingsCsv(auth.accessToken, { ...range, product }).catch((e) => setError(e.message));

  return (
    <AdminShell
      title="Reports"
      subtitle="Profit = what customers paid − refunds to them − (supplier cost − supplier refunds) − payment gateway fees. Failed bookings are left out; their payments were refunded."
      actions={can(user, 'Bookings') && <button type="button" className="btn btn-outline btn-sm" onClick={exportCsv}>Export bookings CSV</button>}
    >
      <div className="admin-filters">
        <div className="field">
          <span>Period</span>
          <div className="admin-presets">
            {presets.map(([label, days]) => {
              const from = daysAgoIso(days);
              const active = range.from === from && range.to === daysAgoIso(0);
              return <button key={label} type="button" className={`chip${active ? ' active' : ''}`} onClick={() => setRange({ from, to: daysAgoIso(0) })}>{label}</button>;
            })}
          </div>
        </div>
        <label className="field"><span>From</span><input type="date" value={range.from} max={range.to} onChange={(e) => e.target.value && setRange({ ...range, from: e.target.value })} /></label>
        <label className="field"><span>To</span><input type="date" value={range.to} min={range.from} onChange={(e) => e.target.value && setRange({ ...range, to: e.target.value })} /></label>
        <label className="field">
          <span>Product</span>
          <select value={product} onChange={(e) => setProduct(e.target.value)}>
            <option value="">All products</option>
            {productTypes.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
      </div>

      {error && <p className="api-message error" role="alert">{error}</p>}
      {!report && !error && <p role="status">Loading report…</p>}
      {report && (
        <>
          <ProfitTiles totals={report.totals} />
          <p className="muted small">
            {report.failedBookings > 0 && `${report.failedBookings} failed booking${report.failedBookings > 1 ? 's' : ''} not counted. `}
            Change requests: {can(user, 'Amendments') ? <Link className="link" to="/admin/amendments?status=Open">{report.amendments.open} new</Link> : `${report.amendments.open} new`},{' '}
            {report.amendments.inProgress} in progress, {report.amendments.awaitingCustomer} awaiting customer.
          </p>

          <section className="panel admin-report-section">
            <h2>Profit by {report.period}</h2>
            <ProfitChart rows={report.byPeriod} period={report.period} />
          </section>

          <section className="admin-report-section">
            <h2>By product</h2>
            <GroupTable rows={report.byProduct} label="Product" name={(k) => productLabel[k] || k} totals={report.totals} />
          </section>
          <section className="admin-report-section">
            <h2>By supplier</h2>
            <GroupTable rows={report.bySupplier} label="Supplier" name={(k) => k} totals={report.totals} />
          </section>
          <section className="admin-report-section">
            <h2>By {report.period}</h2>
            <GroupTable rows={report.byPeriod.filter((r) => r.totals.bookings > 0)} label={report.period === 'day' ? 'Day' : 'Month'} name={(k) => periodLabel(k, report.period)} totals={report.totals} />
          </section>
        </>
      )}
    </AdminShell>
  );
}

const periodLabel = (key, period) => (period === 'day'
  ? new Date(`${key}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
  : new Date(`${key}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }));

function GroupTable({ rows, label, name, totals }) {
  return (
    <div className="admin-table-wrap">
      <table className="admin-table">
        <thead>
          <tr>
            <th>{label}</th><th className="num">Bookings</th><th className="num">Cancelled</th><th className="num">Supplier cost</th>
            <th className="num">Sales</th><th className="num">Markup</th><th className="num">Gateway fees</th><th className="num">Discounts</th>
            <th className="num">Net refunds</th><th className="num">Profit</th><th className="num">Margin</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => <Row key={r.key} name={name(r.key)} t={r.totals} />)}
          {!rows.length && <tr><td colSpan="11">No bookings in this period.</td></tr>}
        </tbody>
        {rows.length > 1 && <tfoot><Row name="Total" t={totals} /></tfoot>}
      </table>
    </div>
  );
}

function Row({ name, t }) {
  return (
    <tr>
      <td><b>{name}</b></td>
      <td className="num">{t.bookings}</td>
      <td className="num">{t.cancelled}</td>
      <td className="num">{money(t.supplierAmount)}</td>
      <td className="num">{money(t.saleAmount)}</td>
      <td className="num">{money(t.markup)}</td>
      <td className="num">{money(t.transactionFees)}</td>
      <td className="num">{money(t.discounts)}</td>
      <td className="num">{money(t.customerRefunds - t.supplierRefunds)}</td>
      <td className={`num ${t.profit < 0 ? 'loss' : 'gain'}`}><b>{money(t.profit)}</b></td>
      <td className="num">{margin(t.profit, t.saleAmount)}</td>
    </tr>
  );
}

/**
 * One series (profit per period) as bars from a zero baseline: gains in the brand colour, losses below the
 * line in coral. Hover or focus a bar for its numbers; the table below holds the same data.
 */
function ProfitChart({ rows, period }) {
  const [hover, setHover] = useState(null);
  const width = 760;
  const height = 220;
  const pad = { top: 16, right: 12, bottom: 28, left: 64 };
  const values = rows.map((r) => r.totals.profit);
  const max = Math.max(0, ...values);
  const min = Math.min(0, ...values);
  const span = max - min || 1;
  const plotH = height - pad.top - pad.bottom;
  const plotW = width - pad.left - pad.right;
  const y = (v) => pad.top + ((max - v) / span) * plotH;
  const slot = plotW / Math.max(rows.length, 1);
  const barW = Math.max(2, Math.min(28, slot - 2));
  const ticks = [max, (max + min) / 2, min].filter((v, i, list) => list.indexOf(v) === i);
  const labelEvery = Math.ceil(rows.length / 10);

  if (!values.some((v) => v !== 0)) return <p className="muted small">No profit or loss in this period yet.</p>;

  return (
    <div className="admin-chart">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`Profit by ${period}`}>
        {ticks.map((v) => (
          <g key={v}>
            <line x1={pad.left} x2={width - pad.right} y1={y(v)} y2={y(v)} className="admin-chart-grid" />
            <text x={pad.left - 8} y={y(v) + 4} textAnchor="end" className="admin-chart-axis">{money(v)}</text>
          </g>
        ))}
        {rows.map((r, i) => {
          const v = r.totals.profit;
          const x = pad.left + i * slot + (slot - barW) / 2;
          const top = Math.min(y(v), y(0));
          const h = Math.max(Math.abs(y(v) - y(0)), v === 0 ? 0 : 1);
          return (
            <g key={r.key} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} onFocus={() => setHover(i)} onBlur={() => setHover(null)} tabIndex="0">
              {/* Hit target spans the whole slot, so thin bars are still easy to hover. */}
              <rect x={pad.left + i * slot} y={pad.top} width={slot} height={plotH} fill="transparent" />
              {h > 0 && <rect x={x} y={top} width={barW} height={h} rx={Math.min(4, barW / 2)} className={v < 0 ? 'admin-chart-loss' : 'admin-chart-gain'} opacity={hover === null || hover === i ? 1 : 0.55} />}
              {i % labelEvery === 0 && <text x={x + barW / 2} y={height - 8} textAnchor="middle" className="admin-chart-axis">{periodLabel(r.key, period)}</text>}
            </g>
          );
        })}
        <line x1={pad.left} x2={width - pad.right} y1={y(0)} y2={y(0)} className="admin-chart-zero" />
      </svg>
      {hover !== null && (
        <div className="admin-chart-tip" style={{ left: `${((pad.left + (hover + 0.5) * slot) / width) * 100}%` }}>
          <b>{periodLabel(rows[hover].key, period)}</b>
          <span>Profit {money(rows[hover].totals.profit)}</span>
          <span className="muted">Sales {money(rows[hover].totals.saleAmount)} · {rows[hover].totals.bookings} bookings</span>
        </div>
      )}
    </div>
  );
}
